import { type FC, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import NotesIcon from "@mui/icons-material/Notes";
import AddIcon from "@mui/icons-material/Add";
import type { CustomerSummary } from "../customerSummariesApi";
import { formatDatePl } from "../formatDatePl";
import type { CustomerNote } from "../../notes/customerNotesApi";
import { useAllCustomerNotes } from "../../notes/useAllCustomerNotes";
import ClientDialogTitle from "./ClientDialogTitle";
import AddClientNoteForm from "./AddClientNoteForm";
import { customerNoteCategoryLabels } from "./customerNoteCategoryLabels";

interface Props {
  client: CustomerSummary | null;
  onClose: () => void;
}

const RECENT_NOTES_LIMIT = 5;

const NoteCard: FC<{ note: CustomerNote }> = ({ note }) => (
  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "rgba(0,0,0,0.015)" }}>
    <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Chip
          size="small"
          label={customerNoteCategoryLabels[note.category]}
          color={note.category === "TRAVEL_PAY" ? "secondary" : "primary"}
          variant="outlined"
        />
        <Typography variant="caption" color="text.secondary">
          {note.author}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {formatDatePl(note.date)}
      </Typography>
    </Stack>
    <Typography variant="body2">{note.text}</Typography>
  </Paper>
);

interface ClientNotesProps {
  client: CustomerSummary;
  adding: boolean;
  onAddingDone: () => void;
}

const ClientNotes: FC<ClientNotesProps> = ({ client, adding, onAddingDone }) => {
  const { notes: allNotes, isLoading } = useAllCustomerNotes(client.id);
  const notes = allNotes.slice(0, RECENT_NOTES_LIMIT);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">{client.name}</Typography>
        <Typography variant="body2" color="text.secondary">
          Ostatnie 5 notatek (Przezmorze / TravelPay)
        </Typography>
      </Box>
      {adding && <AddClientNoteForm customerId={client.id} onDone={onAddingDone} />}
      {notes.map((note) => (
        <NoteCard key={note.id} note={note} />
      ))}
      {isLoading && <LinearProgress />}
      {!isLoading && !notes.length && <Typography color="text.secondary">Brak notatek dla tego klienta.</Typography>}
    </Stack>
  );
};

const ClientNotesDialog: FC<Props> = ({ client, onClose }) => {
  const [adding, setAdding] = useState(false);

  const close = () => {
    setAdding(false);
    onClose();
  };

  return (
    <Dialog open={Boolean(client)} onClose={close} maxWidth="sm" fullWidth>
      <ClientDialogTitle icon={<NotesIcon color="primary" />} title="Ostatnie notatki" onClose={close} />
      <DialogContent dividers>
        {client && <ClientNotes client={client} adding={adding} onAddingDone={() => setAdding(false)} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Zamknij</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAdding(true)} disabled={adding}>
          Dodaj notatkę
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientNotesDialog;
